import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ServiceAdvanceBookingSettings } from '../../components/building-services/ServiceAdvanceBookingSettings';
import {
  buildingServicesApi,
  MovingRegistration,
  MovingRegistrationQuery,
  BuildingServiceSettings,
} from '../../api/buildingServicesApi';
import { buildingsApi, Building } from '../../api/buildingsApi';
import { apartmentsApi, Apartment } from '../../api/apartmentsApi';
import { useAuth } from '../../contexts/AuthContext';
import {applyDefaultBuildingSelection, needsBuildingSelection, shouldShowBuildingSelector, getBuildingSelectProps, selectBuilding } from '../../utils/buildingSelection';
import { useCrudPermissions } from '../../hooks/useCrudPermissions';
import { MOVING_REGISTRATION_PERMISSIONS } from '../../constants/functionPermissions';

const statusOptions = [
  { value: '', label: 'Tất cả trạng thái' },
  { value: 'PENDING_CONFIRMATION', label: 'Chờ xác nhận' },
  { value: 'CONFIRMED', label: 'Đã xác nhận' },
  { value: 'IN_PROGRESS', label: 'Đang vận chuyển' },
  { value: 'COMPLETED', label: 'Hoàn thành' },
  { value: 'REJECTED', label: 'Từ chối' },
  { value: 'CANCELLED', label: 'Đã hủy' },
];

const MovingRegistrationList: React.FC = () => {
  const { user } = useAuth();
  const { canCreate, canEdit } = useCrudPermissions(MOVING_REGISTRATION_PERMISSIONS);
  const [registrations, setRegistrations] = useState<MovingRegistration[]>([]);
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [apartments, setApartments] = useState<Apartment[]>([]);
  const [selectedBuildingId, setSelectedBuildingId] = useState('');
  const [serviceSettings, setServiceSettings] = useState<BuildingServiceSettings | null>(null);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [settingsForm, setSettingsForm] = useState({
    isActive: true,
    regulations: '',
    advanceBookingDays: { min: 0, max: 60 },
    staffAdvanceBookingDays: { min: 0, max: 60 },
  });
  const [filters, setFilters] = useState({
    apartmentId: '', status: '', startDate: '', endDate: '', search: '', page: 1, limit: 25,
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING_CONFIRMATION': return 'bg-yellow-100 text-yellow-800';
      case 'CONFIRMED': return 'bg-blue-100 text-blue-800';
      case 'IN_PROGRESS': return 'bg-purple-100 text-purple-800';
      case 'COMPLETED': return 'bg-green-100 text-green-800';
      case 'REJECTED': return 'bg-red-100 text-red-800';
      case 'CANCELLED': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) =>
    statusOptions.find((opt) => opt.value === status)?.label || status;

  useEffect(() => { loadBuildings(); }, []);

  useEffect(() => {
    if (selectedBuildingId) {
      loadApartments();
      loadServiceSettings();
    } else {
      setApartments([]);
      setServiceSettings(null);
    }
  }, [selectedBuildingId]);

  useEffect(() => {
    if (selectedBuildingId) loadRegistrations();
    else {
      setRegistrations([]);
      setTotal(0);
      setTotalPages(0);
    }
  }, [selectedBuildingId, filters]);

  const loadBuildings = async () => {
    try {
      const response = await buildingsApi.getBuildings();
      setBuildings(response);
      setSelectedBuildingId((prev) => applyDefaultBuildingSelection(response, user, prev));
    } catch {
      toast.error('Lỗi khi tải danh sách tòa nhà');
    }
  };

  const loadApartments = async () => {
    if (!selectedBuildingId) return;
    try {
      const response = await apartmentsApi.getApartments();
      setApartments(response.filter((apt) => apt.buildingId === selectedBuildingId));
    } catch {
      toast.error('Lỗi khi tải danh sách căn hộ');
    }
  };

  const loadServiceSettings = async () => {
    if (!selectedBuildingId) return;
    try {
      const settings = await buildingServicesApi.getServiceSettings(selectedBuildingId, 'MOVING');
      setServiceSettings(settings);
      if (settings) {
        setSettingsForm({
          isActive: settings.isActive,
          regulations: settings.regulations || '',
          advanceBookingDays: settings.advanceBookingDays ?? { min: 0, max: 60 },
          staffAdvanceBookingDays: settings.staffAdvanceBookingDays ?? { min: 0, max: 60 },
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const loadRegistrations = async () => {
    if (!selectedBuildingId) return;
    setLoading(true);
    try {
      const query: MovingRegistrationQuery = { ...filters, buildingId: selectedBuildingId };
      const response = await buildingServicesApi.getMovingRegistrations(query);
      setRegistrations(response.data);
      setTotal(response.total);
      setTotalPages(Math.ceil(response.total / filters.limit));
    } catch {
      toast.error('Lỗi khi tải danh sách đăng ký chuyển đồ');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key: string, value: string | number) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: key !== 'page' ? 1 : (value as number),
    }));
  };

  const saveSettings = async () => {
    if (!selectedBuildingId) return;
    try {
      if (serviceSettings) {
        await buildingServicesApi.updateServiceSettings(selectedBuildingId, 'MOVING', settingsForm);
      } else {
        await buildingServicesApi.createServiceSettings(selectedBuildingId, {
          serviceType: 'MOVING',
          ...settingsForm,
        });
      }
      toast.success('Cập nhật cài đặt thành công');
      setShowSettingsModal(false);
      loadServiceSettings();
    } catch {
      toast.error('Lỗi khi lưu cài đặt');
    }
  };

  const handleAction = async (action: string, registrationId: string, additionalData?: any) => {
    try {
      switch (action) {
        case 'confirm':
          await buildingServicesApi.confirmMoving(registrationId);
          toast.success('Xác nhận đăng ký thành công');
          break;
        case 'reject':
          await buildingServicesApi.rejectMoving(registrationId, additionalData.rejectionReason);
          toast.success('Từ chối đăng ký thành công');
          break;
        case 'start':
          await buildingServicesApi.startMoving(registrationId);
          toast.success('Xác nhận đang vận chuyển thành công');
          break;
        case 'complete':
          await buildingServicesApi.completeMoving(registrationId);
          toast.success('Hoàn thành vận chuyển thành công');
          break;
        case 'cancel':
          await buildingServicesApi.cancelMoving(registrationId);
          toast.success('Hủy đăng ký thành công');
          break;
      }
      loadRegistrations();
    } catch {
      toast.error('Lỗi khi thực hiện thao tác');
    }
  };

  const renderActionButtons = (registration: MovingRegistration) => {
    const canManage = ['SUPERADMIN', 'STAFF', 'OPERATOR'].includes(user?.role || '');
    const isOwner = registration.createdById === user?.id;

    return (
      <div className="flex flex-wrap gap-2">
        {canEdit && (
          <Link to={`/building-services/moving/${registration.id}/edit`} className="text-blue-600 hover:text-blue-800 text-sm">
            Xem
          </Link>
        )}
        {canEdit && canManage && registration.status === 'PENDING_CONFIRMATION' && (
          <>
            <button onClick={() => handleAction('confirm', registration.id)} className="text-green-600 hover:text-green-800 text-sm">Xác nhận</button>
            <button
              onClick={() => {
                const rejectionReason = window.prompt('Nhập lý do từ chối:');
                if (rejectionReason) handleAction('reject', registration.id, { rejectionReason });
              }}
              className="text-red-600 hover:text-red-800 text-sm"
            >
              Từ chối
            </button>
          </>
        )}
        {canEdit && canManage && registration.status === 'CONFIRMED' && (
          <>
            <button onClick={() => handleAction('start', registration.id)} className="text-purple-600 hover:text-purple-800 text-sm">Đang vận chuyển</button>
            <button
              onClick={() => {
                const rejectionReason = window.prompt('Nhập lý do từ chối:');
                if (rejectionReason) handleAction('reject', registration.id, { rejectionReason });
              }}
              className="text-red-600 hover:text-red-800 text-sm"
            >
              Từ chối
            </button>
          </>
        )}
        {canEdit && canManage && registration.status === 'IN_PROGRESS' && (
          <button onClick={() => handleAction('complete', registration.id)} className="text-green-600 hover:text-green-800 text-sm">Hoàn thành</button>
        )}
        {canEdit && ((isOwner && registration.status === 'PENDING_CONFIRMATION') || (canManage && registration.status === 'PENDING_CONFIRMATION')) && (
          <button
            onClick={() => {
              if (window.confirm('Bạn có chắc chắn muốn hủy đăng ký này?')) {
                handleAction('cancel', registration.id);
              }
            }}
            className="text-red-600 hover:text-red-800 text-sm"
          >
            Hủy
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Đăng ký chuyển đồ</h1>
          <p className="text-gray-600">Quản lý đăng ký chuyển đồ trong tòa nhà</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => {
              if (!selectedBuildingId) return toast.error('Vui lòng chọn tòa nhà trước');
              setShowSettingsModal(true);
            }}
            className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700"
          >
            ⚙️ Cài đặt
          </button>
          {canCreate && (
            <Link
              to={selectedBuildingId ? `/building-services/moving/add?buildingId=${selectedBuildingId}` : '#'}
              onClick={(e) => {
                if (!selectedBuildingId) {
                  e.preventDefault();
                  toast.error('Vui lòng chọn tòa nhà trước');
                }
              }}
              className={`px-4 py-2 rounded-lg ${selectedBuildingId ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-400 text-white cursor-not-allowed'}`}
            >
              + Thêm đăng ký chuyển đồ
            </Link>
          )}
        </div>
      </div>

      {shouldShowBuildingSelector(buildings) && (
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-gray-700">Chọn tòa nhà:</label>
            <select
              value={selectedBuildingId}
              onChange={(e) => {
                selectBuilding(user?.id, e.target.value, setSelectedBuildingId);
                setFilters((prev) => ({ ...prev, apartmentId: '', page: 1 }));
              }}
                disabled={!needsBuildingSelection(buildings)}
              className="px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">-- Chọn tòa nhà --</option>
              {buildings.map((building) => (
                <option key={building.id} value={building.id}>{building.name}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {selectedBuildingId && (
        <>
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">Bộ lọc</h3>
              <button onClick={() => setShowFilters(!showFilters)} className="text-blue-600">{showFilters ? 'Ẩn' : 'Hiện'}</button>
            </div>
            {showFilters && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <select value={filters.apartmentId} onChange={(e) => handleFilterChange('apartmentId', e.target.value)} className="border rounded-lg px-3 py-2">
                  <option value="">Tất cả căn hộ</option>
                  {apartments.map((apt) => <option key={apt.id} value={apt.id}>{apt.code} - {apt.ownerName}</option>)}
                </select>
                <select value={filters.status} onChange={(e) => handleFilterChange('status', e.target.value)} className="border rounded-lg px-3 py-2">
                  {statusOptions.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
                <input type="date" value={filters.startDate} onChange={(e) => handleFilterChange('startDate', e.target.value)} className="border rounded-lg px-3 py-2" />
                <input type="date" value={filters.endDate} onChange={(e) => handleFilterChange('endDate', e.target.value)} className="border rounded-lg px-3 py-2" />
                <input
                  type="text"
                  placeholder="Tìm theo đơn vị vận chuyển, SĐT, nội dung..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="md:col-span-2 border rounded-lg px-3 py-2"
                />
              </div>
            )}
          </div>

          <div className="bg-white rounded-lg shadow overflow-x-auto">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-medium">Danh sách đăng ký ({total} kết quả)</h3>
            </div>
            {loading ? (
              <div className="p-8 text-center">Đang tải...</div>
            ) : registrations.length === 0 ? (
              <div className="p-8 text-center text-gray-500">Không có đăng ký chuyển đồ nào</div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">STT</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Căn hộ</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Đơn vị vận chuyển</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Thời gian</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nội dung</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Trạng thái</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Thao tác</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {registrations.map((registration, index) => (
                    <tr key={registration.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm">{(filters.page - 1) * filters.limit + index + 1}</td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium">{registration.apartment.code}</div>
                        <div className="text-sm text-gray-500">{registration.apartment.ownerName}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium">{registration.movingCompanyName}</div>
                        <div className="text-sm text-gray-500">{registration.movingCompanyPhone}</div>
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <div>{new Date(registration.movingDate).toLocaleDateString('vi-VN')}</div>
                        <div className="text-gray-500">{registration.startTime} - {registration.endTime}</div>
                      </td>
                      <td className="px-6 py-4 text-sm max-w-xs truncate">{registration.movingContent}</td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(registration.status)}`}>
                          {getStatusText(registration.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4">{renderActionButtons(registration)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t flex justify-between items-center">
                <span className="text-sm text-gray-700">Trang {filters.page}/{totalPages}</span>
                <div className="flex gap-2">
                  <button disabled={filters.page <= 1} onClick={() => handleFilterChange('page', filters.page - 1)} className="px-3 py-1 border rounded disabled:opacity-50">Trước</button>
                  <button disabled={filters.page >= totalPages} onClick={() => handleFilterChange('page', filters.page + 1)} className="px-3 py-1 border rounded disabled:opacity-50">Sau</button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {showSettingsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
            <h3 className="text-lg font-medium mb-4">Cài đặt dịch vụ đăng ký chuyển đồ</h3>
            <label className="flex items-center mb-4">
              <input type="checkbox" checked={settingsForm.isActive} onChange={(e) => setSettingsForm((prev) => ({ ...prev, isActive: e.target.checked }))} className="rounded" />
              <span className="ml-2 text-sm">Đang hoạt động</span>
            </label>
            <textarea
              value={settingsForm.regulations}
              onChange={(e) => setSettingsForm((prev) => ({ ...prev, regulations: e.target.value }))}
              rows={8}
              className="w-full border rounded-lg px-3 py-2"
              placeholder="Nhập quy định đăng ký chuyển đồ..."
            />
            <ServiceAdvanceBookingSettings
              value={{
                advanceBookingDays: settingsForm.advanceBookingDays,
                staffAdvanceBookingDays: settingsForm.staffAdvanceBookingDays,
              }}
              onChange={(next) =>
                setSettingsForm((prev) => ({
                  ...prev,
                  advanceBookingDays: next.advanceBookingDays,
                  staffAdvanceBookingDays: next.staffAdvanceBookingDays,
                }))
              }
            />
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowSettingsModal(false)} className="px-4 py-2 border rounded-lg">Hủy</button>
              <button onClick={saveSettings} className="px-4 py-2 bg-blue-600 text-white rounded-lg">Lưu</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MovingRegistrationList;
